/**
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
 */
import Vue from 'vue';
import {
  addParticipants,
  createConversation,
  getConversation,
  postConversation,
  removeParticipants,
  renameGroup,
  syncConversations,
  uploadImage
} from '@/api/conversation';
import { $_UTILS } from '@/helpers/utils';

import { CONVERSATION_TYPE, MESSAGE_TYPE, TAB_KEYS, GROUP_EVENTS } from '@/helpers/constants';

function formatParticipantInMessage (message, userPool, currentUser) {
  if (message.sender.userID === currentUser.userhash) {
    message.mine = true;
  } else {
    message.mine = false;
  }
  message.sender = userPool.find(
    user => user.userhash === message.sender.userID
  );
  if (!message.sender) {
    message.sender = {
      fname: 'unknown',
      fullname: 'unknown',
      ifpic: false,
      isMyContact: false,
      iscustom: false,
      isfav: false,
      name: 'unknown',
      presence: false,
      room: null,
      userhash: null,
      userstatus: 0
    };
  }
  if (message.type === CONVERSATION_TYPE.EVENT) {
    if (message.message.eventTarget && (message.message.eventType === GROUP_EVENTS.ADDED ||
      message.message.eventType === GROUP_EVENTS.REMOVED)) {
      message.message.eventTarget = userPool.find(
        user => user.userhash === message.message.eventTarget
      );
      if (!message.message.eventTarget) {
        message.message.eventTarget = {
          fname: 'unknown',
          fullname: 'unknown',
          ifpic: false,
          isMyContact: false,
          iscustom: false,
          isfav: false,
          name: 'unknown',
          presence: false,
          room: null,
          userhash: null,
          userstatus: 0
        };
      }
    }
  }
  return message;
}

function formatParticipants (participants, userPool, currentUser, isGroup) {
  return participants
    .filter(elem => {
      return isGroup ? true : currentUser.userhash !== elem.userID;
    })
    .map(elem => {
      if (currentUser.userhash === elem.userID) {
        return {
          ...currentUser,
          name: currentUser.fullname,
          me: true
        };
      } else {
        return userPool.find(user => user.userhash === elem.userID);
      }
    }).filter(elem => !!elem);
}

function formatMessages (conversation, userPool, currentUser) {
  if (conversation.messages && conversation.messages.length > 0) {
    for (let i = 0; i < conversation.messages.length; i++) {
      formatParticipantInMessage(
        conversation.messages[i],
        userPool,
        currentUser
      );
    }
  }
  return conversation;
}

export default {
  namespaced: true,
  state: {
    conversations: [],
    conversationInfo: null,
    fetchingInfo: false,
    notificationList: [],
    syncing: false
  },
  getters: {
    conversations (state) {
      return state.conversations.map(con => {
        con.lastMessage = con.messages && con.messages.length > 0 ? con.messages[0] : null;
        return con;
      }).sort((left, right) => {
        const ltimestamp = left.messages && left.messages.length
          ? left.messages[0].timestamp
          : left.creationTimestamp;
        const rtimestamp = right.messages && right.messages.length
          ? right.messages[0].timestamp
          : right.creationTimestamp;

        if (ltimestamp > rtimestamp) {
          return -1;
        }

        if (rtimestamp > ltimestamp) {
          return 1;
        }

        return 0;
      });
    },
    conversationInfo (state) {
      return state.conversationInfo;
    },
    fetchingInfo (state) {
      return state.fetchingInfo;
    },
    notificationCount (state) {
      return state.notificationList.length;
    },
    notificationList (state) {
      return state.notificationList;
    },
    syncing (state) {
      return state.syncing;
    }
  },
  mutations: {
    addConversations (state, { conversations, userPool, currentUser }) {
      conversations.forEach(item => {
        const i = state.conversations.findIndex(
          elem => elem.conversationID === item.conversationID
        );
        item.participants = formatParticipants(
          item.participants,
          userPool,
          currentUser,
          item.group
        );
        item = formatMessages(item, userPool, currentUser);
        if (i >= 0) {
          Vue.set(state.conversations, i, item);
        } else {
          state.conversations.push(item);
        }
      });
    },
    addMessageToConversationsList (state, data) {
      const i = state.conversations.findIndex(
        elem => elem.conversationID === data.conversationID
      );
      if (i >= 0) {
        const newData = state.conversations[i];
        if (newData) {
          newData.messages = [data.message];
          Vue.set(state.conversations, i, newData);
        }
      }
    },
    addNotification (state, conversationID) {
      if (!conversationID) {
        return;
      }
      if (!state.notificationList.find(elem => elem === conversationID)) {
        state.notificationList.push(conversationID);
      }
    },
    checkNotifications (state) {
      for (let i = 0; i < state.notificationList.length; i++) {
        if (state.conversations.find(elem => elem.conversationID === state.notificationList[i])) {
          state.notificationList.splice(i, 1);
          i--;
        }
      }
    },
    fetchingInfo (state, status) {
      state.fetchingInfo = status;
    },
    removeConversation (state, conversation) {
      const i = state.conversations.findIndex(
        elem => elem.conversationID === conversation.conversationID
      );
      if (i >= 0) {
        state.conversations.splice(i, 1);
      }
    },
    removeNotification (state, conversation) {
      if (!conversation) {
        return;
      }
      const index = state.notificationList.findIndex(elem => elem === conversation.conversationID);
      if (index >= 0) {
        state.notificationList.splice(index, 1);
      }
    },
    resetConversations (state) {
      state.conversations = [];
      state.conversationInfo = null;
      state.fetchingInfo = false;
      state.syncing = false;
      state.notificationList = [];
    },
    setConversationInfo (state, conversation) {
      state.conversationInfo = conversation;
    },
    setMessage (state, { message, messageID }) {
      if (state.conversationInfo && state.conversationInfo.messages) {
        const index = state.conversationInfo.messages.findIndex(
          msg => msg.messageID === messageID
        );
        if (index < 0) {
          state.conversationInfo.messages.push(message);
        } else {
          if (messageID !== message.messageID) {
            const msgIndex = state.conversationInfo.messages.findIndex(
              msg => msg.messageID === message.messageID
            );
            if (msgIndex >= 0) {
              state.conversationInfo.messages.splice(index, 1);
              return;
            }
          }
          Vue.set(state.conversationInfo.messages, index, message);
        }
      }
    },
    syncing (state, status) {
      state.syncing = status;
    },
    updateConversation (
      state,
      { conversation, newData, userPool, currentUser }
    ) {
      const i = state.conversations.findIndex(
        elem => elem.conversationID === conversation.conversationID
      );
      if (i >= 0) {
        const item = state.conversations[i];
        if (newData && newData.participants) {
          item.participants = formatParticipants(
            newData.participants,
            userPool,
            currentUser,
            item.group
          );
        }
        if (newData && newData.name) {
          item.name = newData.name;
        } else {
          item.name = null;
        }
        Vue.set(state.conversations, i, item);
      }
    }
  },
  actions: {
    async addConversations (
      { commit, state, getters, rootGetters, dispatch },
      conversations
    ) {
      if (conversations && conversations.length > 0) {
        const users = conversations.reduce((uids, conversation) => {
          conversation.participants.forEach(elem => {
            uids.push({ userhash: elem.userID });
          });
          conversation.messages.forEach(element => {
            if (!uids.find(elem => elem.userhash === element.sender.userID)) {
              uids.push({ userhash: element.sender.userID });
            }
            if (element.type === CONVERSATION_TYPE.EVENT &&
              element.message.eventTarget &&
              (element.message.eventType === GROUP_EVENTS.ADDED ||
                element.message.eventType === GROUP_EVENTS.REMOVED)) {
              uids.push({ userhash: element.message.eventTarget });
            }
          });
          return uids;
        }, []);
        await dispatch('ContactStore/fetchIfNotExist', users, { root: true });
        await commit('addConversations', {
          conversations,
          userPool: rootGetters['ContactStore/userPool'],
          currentUser: rootGetters['AuthStore/profile']
        });
        if (!state.conversationInfo && getters.conversations && getters.conversations.length > 0) {
          await dispatch('setConversationInfo', getters.conversations[0]);
        }
      }
    },
    async addMessage ({ commit, state, rootGetters, dispatch }, msg) {
      let con = state.conversations.find(elem => elem.conversationID === msg.recipient.conversationID);
      if (!con) {
        try {
          con = await getConversation(msg.recipient.conversationID);
        } catch (error) {
          if (error.response && error.response.status && error.response.status === 403) {
            await commit('setConversationInfo', null);
            await dispatch('conversationRemoved', msg.recipient.conversationID);
          }
        }
        if (con) {
          await dispatch(
            'addConversations',
            [con]
          );
        } else {
          return;
        }
      }
      if (rootGetters['ContactStore/userPool'].userhash !== msg.sender.userID) {
        await dispatch(
          'ContactStore/fetchIfNotExist',
          [{ userhash: msg.sender.userID }],
          { root: true }
        );
      }
      msg = formatParticipantInMessage(
        msg,
        rootGetters['ContactStore/userPool'],
        rootGetters['AuthStore/profile']
      );

      await commit('addMessageToConversationsList', {
        conversationID: msg.recipient.conversationID,
        message: msg
      });
      if (
        state.conversationInfo &&
        state.conversationInfo.conversationID === msg.recipient.conversationID
      ) {
        await commit('setMessage', {
          messageID: msg.messageID,
          message: msg
        });
      }
    },
    async addNotification ({ commit }, conversationID) {
      await commit('addNotification', conversationID);
    },
    async addParticipants (
      { commit, rootGetters, dispatch },
      { conversation, userHashs }
    ) {
      if (
        conversation &&
        conversation.conversationID &&
        userHashs &&
        userHashs.length > 0
      ) {
        const result = await addParticipants(
          conversation.conversationID,
          userHashs
        );
        const out = await dispatch('updateConversation', {
          conversation,
          newData: result
        });

        return out;
      }
      return false;
    },
    async createConversation (
      { commit, dispatch },
      { userHashes, isGroup, name }
    ) {
      try {
        const conversation = await createConversation(
          userHashes,
          !!isGroup,
          name
        );
        if (conversation) {
          await dispatch('addConversations', [conversation]);
          await dispatch('setConversationInfo', conversation);
        }
      } catch (error) {

      }
    },
    async postTextMessage (
      { commit, rootGetters, dispatch },
      { conversation, message }
    ) {
      const messageID = `${Date.now()}${$_UTILS.getRandomInt(100, 999999)}`;
      const msg = {
        messageID: messageID,
        type: CONVERSATION_TYPE.MESSAGE,
        recipient: {
          conversationID: conversation.conversationID
        },
        sender: {
          userID: rootGetters['AuthStore/profile'].userhash
        },
        message: {
          type: MESSAGE_TYPE.TEXT,
          value: message
        },
        mine: true,
        timestamp: new Date().toISOString(),
        status: {
          sending: true,
          created: false,
          sent: false,
          received: false,
          seen: false,
          error: false,
          uploading: false
        }
      };
      await commit('setMessage', {
        messageID: messageID,
        message: msg
      });
      try {
        const result = await postConversation(
          CONVERSATION_TYPE.MESSAGE,
          conversation.conversationID,
          MESSAGE_TYPE.TEXT,
          message
        );
        if (result) {
          const data = {
            messageID: messageID,
            message: {
              mine: true,
              status: {
                sending: false,
                created: true,
                sent: false,
                received: false,
                seen: false,
                error: false,
                uploading: false
              },
              ...result
            }
          };
          await commit('setMessage', data);
          await commit('addMessageToConversationsList', {
            conversationID: result.recipient.conversationID,
            message: result
          });
        }
      } catch (error) {
        await commit('setMessage', {
          messageID: messageID,
          message: {
            ...msg,
            status: {
              sending: false,
              created: false,
              sent: false,
              received: false,
              seen: false,
              error: true,
              uploading: false
            }
          }
        });
      }
    },
    async postPictureMessage (
      { commit, rootGetters, dispatch },
      { conversation, image }
    ) {
      if (image) {
        var reader = new FileReader();
        reader.onload = async (event) => {
          const messageID = `${Date.now()}${$_UTILS.getRandomInt(100, 999999)}`;
          const msg = {
            messageID: messageID,
            type: CONVERSATION_TYPE.MESSAGE,
            recipient: {
              conversationID: conversation.conversationID
            },
            sender: {
              userID: rootGetters['AuthStore/profile'].userhash
            },
            message: {
              type: MESSAGE_TYPE.IMG,
              value: event.target.result
            },
            mine: true,
            timestamp: new Date().toISOString(),
            status: {
              sending: true,
              created: false,
              sent: false,
              received: false,
              seen: false,
              error: false,
              uploading: true
            }
          };
          await commit('setMessage', {
            messageID: messageID,
            message: msg
          });
          try {
            const result = await uploadImage(
              conversation.conversationID,
              image,
              async (progress) => {
                msg.status.progress = progress;
                await commit('setMessage', {
                  messageID: messageID,
                  message: {
                    ...msg
                  }
                });
              }
            );
            if (result) {
              const data = {
                messageID: messageID,
                message: {
                  mine: true,
                  status: {
                    sending: false,
                    created: true,
                    sent: false,
                    received: false,
                    seen: false,
                    error: false,
                    uploading: false
                  },
                  ...result
                }
              };
              await commit('setMessage', data);
              await commit('addMessageToConversationsList', {
                conversationID: result.recipient.conversationID,
                message: result
              });
            }
          } catch (error) {
            await commit('setMessage', {
              messageID: messageID,
              message: {
                ...msg,
                status: {
                  sending: false,
                  created: false,
                  sent: false,
                  received: false,
                  seen: false,
                  error: true,
                  uploading: false
                }
              }
            });
          }
        };
        reader.readAsDataURL(image);
      }
    },
    async removeNotification ({ commit }, conversation) {
      await commit('removeNotification', conversation);
    },
    async removeParticipant (
      { commit, state, rootGetters, dispatch },
      { conversation, userHash }
    ) {
      if (
        conversation &&
        conversation.conversationID &&
        userHash
      ) {
        const result = await removeParticipants(
          conversation.conversationID,
          [userHash]
        );
        if (!result) {
          return false;
        }
        let out = false;

        if (rootGetters['AuthStore/profile'].userhash !== userHash) {
          out = await dispatch('updateConversation', {
            conversation,
            newData: result
          });
        } else {
          await commit('removeConversation', conversation);
          dispatch('setConversationInfo', state.conversations ? (state.conversations[0] || null) : null);
          out = true;
        }
        return out;
      }
      return false;
    },
    resetConversations ({ commit }) {
      commit('resetConversations');
    },
    async setConversationInfo ({ commit, rootGetters, dispatch }, conversation) {
      if (conversation) {
        await commit('fetchingInfo', true);
        let info = null;
        try {
          info = await getConversation(conversation.conversationID);
          if (info) {
            const users = info.participants.map(elem => {
              return { userhash: elem.userID };
            });
            info.messages.forEach(element => {
              if (!users.find(elem => elem.userhash === element.sender.userID)) {
                users.push({ userhash: element.sender.userID });
              }
              if (element.type === CONVERSATION_TYPE.EVENT &&
                element.message.eventTarget &&
                (element.message.eventType === GROUP_EVENTS.ADDED ||
                  element.message.eventType === GROUP_EVENTS.REMOVED)) {
                users.push({ userhash: element.message.eventTarget });
              }
            });
            await dispatch('ContactStore/fetchIfNotExist', users, { root: true });

            info.participants = formatParticipants(
              info.participants,
              rootGetters['ContactStore/userPool'],
              rootGetters['AuthStore/profile'],
              info.group
            );
            info = formatMessages(
              info,
              rootGetters['ContactStore/userPool'],
              rootGetters['AuthStore/profile']
            );
            await commit('setConversationInfo', info);
          }
          if (rootGetters['AppStore/activeTab'] === TAB_KEYS.CHATS) {
            await commit('removeNotification', conversation);
          }
        } catch (error) {
          if (error.response && error.response.status && error.response.status === 403) {
            await commit('setConversationInfo', null);
            await dispatch('conversationRemoved', conversation.conversationID);
          }
        }
        await commit('fetchingInfo', false);
      } else {
        await commit('setConversationInfo', null);
      }
    },
    async syncConversations ({ commit, getters, dispatch }) {
      await commit('syncing', true);
      try {
        const conversations = await syncConversations();
        if (conversations) {
          await dispatch('addConversations', conversations);
          if (getters.conversations && getters.conversations.length > 0) {
            await dispatch('setConversationInfo', getters.conversations[0]);
          }
        }
        await commit('checkNotifications');
      } catch (error) {

      }
      await commit('syncing', false);
    },
    async updateConversation (
      { commit, state, rootGetters, dispatch },
      { conversation, newData }
    ) {
      if (newData) {
        if (newData && newData.participants) {
          const users = newData.participants.map(elem => {
            return { userhash: elem.userID };
          });

          await dispatch('ContactStore/fetchIfNotExist', users, {
            root: true
          });
        }
        await commit('updateConversation', {
          conversation,
          newData,
          userPool: rootGetters['ContactStore/userPool'],
          currentUser: rootGetters['AuthStore/profile']
        });
        if (
          state.conversationInfo &&
          state.conversationInfo.conversationID === conversation.conversationID
        ) {
          await dispatch('setConversationInfo', conversation);
        }
        return true;
      }
      return false;
    },
    async updateGroupName ({ commit, state, rootGetters, dispatch }, { conversation, name }) {
      const result = await renameGroup(
        conversation.conversationID,
        name
      );

      if (!result) {
        return false;
      }

      const out = await dispatch('updateConversation', {
        conversation,
        newData: result
      });

      return out;
    },
    async conversationRemoved ({ commit }, conversationID) {
      await commit('removeConversation', { conversationID });
      return conversationID;
    }
  }
};
